import React, { useEffect, useMemo, useState } from "react";
import ComplianceTaskRow from "./ComplianceTaskRow";

const defaultTaskDraft = (timelineTypes, frequencyOptions) => ({
  title: "",
  description: "",
  timelineType: timelineTypes.DEFINITE,
  startDate: "",
  deadline: "",
  nextCheckDate: "",
  frequency: frequencyOptions[0]?.value || "monthly",
  dependency: "",
  notes: "",
});

const normalizeWeight = (value) => {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : 0;
};

const roundToTwo = (value) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) {
    return 0;
  }
  return Math.round(numeric * 100) / 100;
};

const formatWeight = (value) => roundToTwo(value).toFixed(2);

const ComplianceCategoryCard = ({
  corporateId,
  category,
  statusOptions,
  lifecycleOptions,
  frequencyOptions,
  timelineTypes,
  isCompanyView = false,
  availableUsers,
  canEditScheduling = false,
  canAssignResponsibility = false,
  checklistHeaderEditable = false,
  canEditLifecycle = false,
  statusEditableIds = [],
  onAddTask,
  onUpdateTask,
  onLifecycleChange,
  onAssignResponsibility,
  onWeightChange,
  isOpen,
  onToggle,
}) => {
  const [showTaskForm, setShowTaskForm] = useState(false);
  const [taskDraft, setTaskDraft] = useState(() =>
    defaultTaskDraft(timelineTypes, frequencyOptions)
  );
  const [weightDraft, setWeightDraft] = useState(category.weight);
  const [expandedTaskId, setExpandedTaskId] = useState(null);

  useEffect(() => {
    setWeightDraft(category.weight);
  }, [category.weight]);

  useEffect(() => {
    if (!isOpen) {
      setShowTaskForm(false);
      setExpandedTaskId(null);
    }
  }, [isOpen]);

  const handleAddTask = (event) => {
    event.preventDefault();
    if (!taskDraft.title) return;
    const payload = {
      title: taskDraft.title,
      description: taskDraft.description,
      timelineType: taskDraft.timelineType,
      startDate: taskDraft.startDate || null,
      deadline:
        taskDraft.timelineType === timelineTypes.DEFINITE
          ? taskDraft.deadline || null
          : null,
      nextCheckDate:
        taskDraft.timelineType === timelineTypes.INDEFINITE
          ? taskDraft.nextCheckDate || null
          : null,
      frequency:
        taskDraft.timelineType === timelineTypes.INDEFINITE
          ? taskDraft.frequency
          : null,
      dependency: taskDraft.dependency || null,
      notes: taskDraft.notes || null,
    };
    onAddTask(category.id, payload);
    setTaskDraft(defaultTaskDraft(timelineTypes, frequencyOptions));
    setShowTaskForm(false);
  };

  const handleWeightBlur = () => {
    onWeightChange(category.id, weightDraft);
  };

  const tasks = useMemo(() => category.tasks || [], [category.tasks]);

  useEffect(() => {
    if (!expandedTaskId) return;
    if (!tasks.some((task) => task.id === expandedTaskId)) {
      setExpandedTaskId(null);
    }
  }, [tasks, expandedTaskId]);

  const { completedWeight, uncompletedWeight } = useMemo(() => {
    const totalWeight = normalizeWeight(category.weight);
    const completed = tasks.reduce((sum, task) => {
      if (task.lifecycleStatus !== "active") return sum;
      if (task.summaryStatus === "compliant") {
        return sum + normalizeWeight(task.weight);
      }
      return sum;
    }, 0);

    const safeCompleted = Math.min(totalWeight, roundToTwo(completed));
    const remaining = Math.max(0, roundToTwo(totalWeight - safeCompleted));

    return {
      completedWeight: safeCompleted,
      uncompletedWeight: remaining,
    };
  }, [category.weight, tasks]);

  const handleToggle = () => {
    if (typeof onToggle === "function") {
      onToggle();
    }
  };

  const handleTaskToggle = (taskId) => {
    setExpandedTaskId((current) => (current === taskId ? null : taskId));
  };

  return (
    <section className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-2 md:flex-row md:items-start md:justify-between">
          <button
            type="button"
            onClick={handleToggle}
            aria-expanded={Boolean(isOpen)}
            className="flex flex-1 items-start gap-3 text-left focus:outline-none"
          >
            <span
              className={`mt-1 inline-flex h-5 w-5 items-center justify-center rounded-full bg-gray-100 text-sm font-semibold text-gray-600 transition-transform duration-150 ease-in-out ${
                isOpen ? "rotate-90" : ""
              } transform`}
            >
              ▸
            </span>
            <div>
              <h3 className="text-lg font-semibold text-gray-800">
                {category.name}
              </h3>
              {category.description && (
                <p className="text-sm text-gray-600">{category.description}</p>
              )}
            </div>
          </button>
          <div className="flex flex-col items-end gap-2">
            <label className="text-xs font-semibold uppercase tracking-wide text-gray-500">
              Category Weight (%)
            </label>
            <input
              type="number"
              min="0"
              max="100"
              step="0.01"
              value={weightDraft}
              onChange={(event) => setWeightDraft(event.target.value)}
              onBlur={handleWeightBlur}
              className="w-28 rounded-md border border-gray-200 px-3 py-2 text-sm focus:border-sflPrimary focus:outline-none"
            />
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-3 text-xs font-semibold text-gray-600">
          <span className="rounded-full bg-emerald-50 px-3 py-1 text-emerald-700">
            Completed Weight: {formatWeight(completedWeight)}%
          </span>
          <span className="rounded-full bg-amber-50 px-3 py-1 text-amber-700">
            Uncompleted Weight: {formatWeight(uncompletedWeight)}%
          </span>
        </div>
        {isOpen && (
          <>
            <div className="space-y-5">
              {tasks.map((task) => {
                const canChangeStatus = (task?.responsibilities || []).some(
                  (resp) =>
                    statusEditableIds.includes(String(resp?.individualId))
                );
                return (
                  <ComplianceTaskRow
                    key={task.id}
                    corporateId={corporateId}
                    categoryId={category.id}
                    task={task}
                    statusOptions={statusOptions}
                    lifecycleOptions={lifecycleOptions}
                    frequencyOptions={frequencyOptions}
                    timelineTypes={timelineTypes}
                    availableUsers={availableUsers}
                    isCompanyView={isCompanyView}
                    canEditScheduling={canEditScheduling}
                    canAssignResponsibility={canAssignResponsibility}
                    checklistHeaderEditable={checklistHeaderEditable}
                    canChangeStatus={canChangeStatus}
                    canEditLifecycle={canEditLifecycle}
                    onUpdateTask={onUpdateTask}
                    onLifecycleChange={onLifecycleChange}
                    onAssignResponsibility={onAssignResponsibility}
                    isOpen={expandedTaskId === task.id}
                    onToggle={() => handleTaskToggle(task.id)}
                  />
                );
              })}
            </div>
            <div className="mt-2">
              {isCompanyView ? null : showTaskForm ? (
                <form
                  onSubmit={handleAddTask}
                  className="space-y-3 rounded-xl border border-dashed border-sflPrimary/40 bg-slate-50 p-4"
                >
                  <h4 className="text-sm font-semibold text-gray-700">
                    Add New Task
                  </h4>
                  <div className="grid gap-3 md:grid-cols-2">
                    <div className="md:col-span-2">
                      <label className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                        Task Title
                      </label>
                      <input
                        type="text"
                        value={taskDraft.title}
                        onChange={(event) =>
                          setTaskDraft((prev) => ({
                            ...prev,
                            title: event.target.value,
                          }))
                        }
                        required
                        className="mt-1 w-full rounded-md border border-gray-200 px-3 py-2 text-sm focus:border-sflPrimary focus:outline-none"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                        Description
                      </label>
                      <textarea
                        value={taskDraft.description}
                        onChange={(event) =>
                          setTaskDraft((prev) => ({
                            ...prev,
                            description: event.target.value,
                          }))
                        }
                        rows={3}
                        className="mt-1 w-full rounded-md border border-gray-200 px-3 py-2 text-sm focus:border-sflPrimary focus:outline-none"
                      />
                    </div>
                    <div>
                      <label className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                        Timeline Type
                      </label>
                      <select
                        value={taskDraft.timelineType}
                        onChange={(event) =>
                          setTaskDraft((prev) => {
                            const nextTimelineType = event.target.value;
                            if (nextTimelineType === timelineTypes.DEFINITE) {
                              return {
                                ...prev,
                                timelineType: nextTimelineType,
                                deadline: prev.deadline || prev.nextCheckDate,
                                nextCheckDate: "",
                                frequency:
                                  prev.frequency ||
                                  frequencyOptions[0]?.value ||
                                  "monthly",
                              };
                            }
                            return {
                              ...prev,
                              timelineType: nextTimelineType,
                              nextCheckDate:
                                prev.nextCheckDate || prev.deadline,
                              deadline: "",
                              frequency:
                                prev.frequency ||
                                frequencyOptions[0]?.value ||
                                "monthly",
                            };
                          })
                        }
                        className="mt-1 w-full rounded-md border border-gray-200 px-3 py-2 text-sm focus:border-sflPrimary focus:outline-none"
                      >
                        {Object.values(timelineTypes).map((type) => (
                          <option key={type} value={type}>
                            {type === timelineTypes.DEFINITE
                              ? "Definite"
                              : "Indefinite"}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                        Start Date
                      </label>
                      <input
                        type="date"
                        value={taskDraft.startDate}
                        onChange={(event) =>
                          setTaskDraft((prev) => ({
                            ...prev,
                            startDate: event.target.value,
                          }))
                        }
                        className="mt-1 w-full rounded-md border border-gray-200 px-3 py-2 text-sm focus:border-sflPrimary focus:outline-none"
                      />
                    </div>
                    {taskDraft.timelineType === timelineTypes.DEFINITE ? (
                      <div>
                        <label className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                          Deadline
                        </label>
                        <input
                          type="date"
                          value={taskDraft.deadline}
                          onChange={(event) =>
                            setTaskDraft((prev) => ({
                              ...prev,
                              deadline: event.target.value,
                            }))
                          }
                          className="mt-1 w-full rounded-md border border-gray-200 px-3 py-2 text-sm focus:border-sflPrimary focus:outline-none"
                        />
                      </div>
                    ) : (
                      <>
                        <div>
                          <label className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                            Frequency
                          </label>
                          <select
                            value={taskDraft.frequency}
                            onChange={(event) =>
                              setTaskDraft((prev) => ({
                                ...prev,
                                frequency: event.target.value,
                              }))
                            }
                            className="mt-1 w-full rounded-md border border-gray-200 px-3 py-2 text-sm focus:border-sflPrimary focus:outline-none"
                          >
                            {frequencyOptions.map((option) => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                            Next Check Date
                          </label>
                          <input
                            type="date"
                            value={taskDraft.nextCheckDate}
                            onChange={(event) =>
                              setTaskDraft((prev) => ({
                                ...prev,
                                nextCheckDate: event.target.value,
                              }))
                            }
                            className="mt-1 w-full rounded-md border border-gray-200 px-3 py-2 text-sm focus:border-sflPrimary focus:outline-none"
                          />
                        </div>
                      </>
                    )}
                    <div className="md:col-span-2">
                      <label className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                        Dependency
                      </label>
                      <input
                        type="text"
                        value={taskDraft.dependency}
                        onChange={(event) =>
                          setTaskDraft((prev) => ({
                            ...prev,
                            dependency: event.target.value,
                          }))
                        }
                        className="mt-1 w-full rounded-md border border-gray-200 px-3 py-2 text-sm focus:border-sflPrimary focus:outline-none"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                        Notes
                      </label>
                      <textarea
                        value={taskDraft.notes}
                        onChange={(event) =>
                          setTaskDraft((prev) => ({
                            ...prev,
                            notes: event.target.value,
                          }))
                        }
                        rows={2}
                        className="mt-1 w-full rounded-md border border-gray-200 px-3 py-2 text-sm focus:border-sflPrimary focus:outline-none"
                      />
                    </div>
                  </div>
                  <div className="flex justify-end gap-2">
                    <button
                      type="button"
                      onClick={() => {
                        setTaskDraft(
                          defaultTaskDraft(timelineTypes, frequencyOptions)
                        );
                        setShowTaskForm(false);
                      }}
                      className="rounded-md border border-gray-200 px-4 py-2 text-xs font-semibold text-gray-600"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="rounded-md bg-sflPrimary px-4 py-2 text-xs font-semibold text-white"
                    >
                      Save Task
                    </button>
                  </div>
                </form>
              ) : isCompanyView ? null : (
                <button
                  type="button"
                  onClick={() => setShowTaskForm(true)}
                  className="text-sm font-semibold text-sflPrimary hover:underline"
                >
                  + Add Task
                </button>
              )}
            </div>
          </>
        )}
      </div>
    </section>
  );
};

export default ComplianceCategoryCard;
